import { useState, useEffect, useRef } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import useAuthStore from '../../store/authStore'
import { authService } from '../../services/authService'
import { useTranslation } from 'react-i18next'
import Menu from '@mui/icons-material/Menu'
import LanguageSwitcher from './LanguageSwitcher'
import { localizeDigits } from '../../utils/uiNumerals'

const CLOSE_ICON = '/assets/images/ui_elements/icon_close.png'

const Layout = () => {
  const { user, clearAuth, updateUser, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const uiLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0].toLowerCase()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const mainScrollRef = useRef(null)

  /** SPA: scroll container is <main>, not window — reset on every in-app navigation */
  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.pathname])

  const handleLogout = () => {
    setMobileMenuOpen(false)
    clearAuth()
    navigate('/')
  }

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  /** Login often only stores tokens; /auth/me fills role + parent/child for nav (e.g. My children). */
  useEffect(() => {
    if (!isAuthenticated) return
    authService
      .getCurrentUser()
      .then((profile) => updateUser(profile))
      .catch(() => {})
  }, [isAuthenticated, updateUser])

  useEffect(() => {
    if (!mobileMenuOpen) return
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileMenuOpen])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  const navLinkClass =
    'flex items-center gap-3 px-3 py-3 rounded-xl text-base font-medium text-gray-800 hover:bg-white/60 hover:text-primary-700 transition-colors touch-manipulation'

  const navIconClass = 'w-8 h-8 object-contain shrink-0'

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col print:bg-white">
      <nav className="bg-white shadow-sm sticky top-0 z-50 shrink-0 border-b border-gray-100/80 print:hidden">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center gap-2 md:gap-3 min-h-[3.5rem] md:min-h-[3.75rem] py-2 md:py-0">
            {/* Mobile: hamburger + brand */}
            <div className="flex items-center gap-2 shrink-0 min-w-0">
              <button
                type="button"
                className="md:hidden flex items-center justify-center rounded-lg p-2 -ms-1 text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors touch-manipulation"
                onClick={() => setMobileMenuOpen(true)}
                aria-expanded={mobileMenuOpen}
                aria-controls="dashboard-mobile-menu"
                aria-label={t('common.menu', 'Menu')}
              >
                <Menu sx={{ fontSize: 30 }} />
              </button>

              <Link
                to="/dashboard"
                className="flex items-center gap-2 sm:gap-2.5 shrink-0 min-w-0"
                onClick={() => setMobileMenuOpen(false)}
              >
                <img
                  src="/favicon.png"
                  alt={t('common.appName')}
                  className="h-11 w-11 sm:h-12 sm:w-12 md:h-10 md:w-10 object-contain rounded-full"
                  onError={(e) => {
                    e.target.src = '/assets/images/ui_elements/logo.png'
                    e.target.onerror = null
                    e.target.classList.remove('rounded-full')
                  }}
                />
                <span className="text-base sm:text-lg md:text-xl font-bold text-gray-900 tracking-tight truncate whitespace-nowrap">
                  {t('common.appName')}
                </span>
              </Link>
            </div>

            {/* Desktop nav: scroll if needed, never wrap labels */}
            <div
              className="hidden md:flex flex-1 min-w-0 items-center overflow-x-auto overscroll-x-contain py-1 -my-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300/80"
              role="navigation"
              aria-label={t('common.menu', 'Menu')}
            >
              <div className="flex items-center gap-0.5 lg:gap-1 flex-nowrap pe-1">
                <Link
                  to="/dashboard"
                  className="flex shrink-0 items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50/80 transition-colors whitespace-nowrap"
                  title={t('common.home', 'Home')}
                >
                  <img
                    src="/assets/images/ui_elements/icon_home.png"
                    alt=""
                    className="w-6 h-6 lg:w-7 lg:h-7 object-contain shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                  <span>{t('common.home', 'Home')}</span>
                </Link>
                <Link
                  to="/draw"
                  className="flex shrink-0 items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50/80 transition-colors whitespace-nowrap"
                  title={t('common.draw')}
                >
                  <img
                    src="/assets/images/ui_elements/icon_draw.png"
                    alt=""
                    className="w-6 h-6 lg:w-7 lg:h-7 object-contain shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                  <span>{t('common.draw')}</span>
                </Link>
                {user?.role === 'parent' && (
                  <Link
                    to="/children"
                    className="flex shrink-0 items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50/80 transition-colors whitespace-nowrap"
                    title={t('common.navChildren', 'My children')}
                  >
                    <img
                      src="/assets/images/ui_elements/icon_profile.png"
                      alt=""
                      className="w-6 h-6 lg:w-7 lg:h-7 object-contain shrink-0"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                    <span className="hidden 2xl:inline">{t('common.navChildren', 'My children')}</span>
                    <span className="2xl:hidden">{t('common.navChildrenShort', 'Children')}</span>
                  </Link>
                )}
                {(user?.role === 'school_admin' || user?.role === 'teacher') && (
                  <Link
                    to="/school/students"
                    className="flex shrink-0 items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50/80 transition-colors whitespace-nowrap"
                    title={t('common.navSchoolStudents', 'School students')}
                  >
                    <img
                      src="/assets/images/ui_elements/icon_profile.png"
                      alt=""
                      className="w-6 h-6 lg:w-7 lg:h-7 object-contain shrink-0"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                    <span className="hidden 2xl:inline">{t('common.navSchoolStudents', 'School students')}</span>
                    <span className="2xl:hidden">{t('common.navSchoolStudentsShort', 'Students')}</span>
                  </Link>
                )}
                <Link
                  to="/album"
                  className="flex shrink-0 items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50/80 transition-colors whitespace-nowrap"
                  title={t('common.album')}
                >
                  <img
                    src="/assets/images/ui_elements/icon_album.png"
                    alt=""
                    className="w-6 h-6 lg:w-7 lg:h-7 object-contain shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                  <span>{t('common.album')}</span>
                </Link>
                <Link
                  to="/dashboard/progress"
                  className="flex shrink-0 items-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50/80 transition-colors whitespace-nowrap"
                  title={t('dashboard.navProgress')}
                >
                  <img
                    src="/assets/images/ui_elements/icon_success.png"
                    alt=""
                    className="w-6 h-6 lg:w-7 lg:h-7 object-contain shrink-0"
                    onError={(e) => {
                      e.target.style.display = 'none'
                    }}
                  />
                  <span>{t('dashboard.navProgress')}</span>
                </Link>
              </div>
            </div>

            {/* Desktop: language + user + logout */}
            <div className="hidden md:flex items-center gap-2 lg:gap-3 shrink-0 ps-2 md:ps-3 border-s border-gray-200/90">
              <LanguageSwitcher />
              <span
                className="text-xs lg:text-sm text-gray-600 hidden xl:inline-block truncate max-w-[9rem] 2xl:max-w-[14rem]"
                title={user?.email || ''}
              >
                {user?.email ? localizeDigits(user.email, uiLang) : ''}
              </span>
              <button
                type="button"
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-2 lg:px-2.5 py-2 rounded-lg text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-gray-50 transition-colors touch-manipulation whitespace-nowrap"
                title={t('common.logout')}
              >
                <img
                  src="/assets/images/ui_elements/icon_settings.png"
                  alt=""
                  className="w-6 h-6 lg:w-7 lg:h-7 object-contain shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none'
                  }}
                />
                <span className="hidden lg:inline">{t('common.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile drawer + overlay — hidden when printing (activity / other print-friendly pages) */}
      <div className="md:hidden print:hidden">
        <div
          className={`fixed inset-0 z-[60] bg-black/35 backdrop-blur-sm transition-opacity duration-300 ease-out ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-hidden={!mobileMenuOpen}
          onClick={() => setMobileMenuOpen(false)}
        />
        <aside
          id="dashboard-mobile-menu"
          lang={i18n.language}
          className={`fixed top-0 bottom-0 start-0 z-[70] flex w-[min(88vw,20rem)] max-w-full flex-col border-e border-primary-200/40 bg-gradient-to-b from-emerald-50/95 via-white/80 to-emerald-50/90 shadow-2xl backdrop-blur-xl transition-transform duration-300 ease-out ${
            mobileMenuOpen
              ? 'translate-x-0'
              : '-translate-x-full pointer-events-none rtl:translate-x-full rtl:pointer-events-none'
          }`}
          aria-hidden={!mobileMenuOpen}
        >
          <div className="relative flex items-center justify-between border-b border-primary-200/30 px-4 py-4">
            <span className="text-lg font-bold text-primary-700">{t('common.menu', 'Menu')}</span>
            <button
              type="button"
              className="rounded-lg p-2 text-gray-600 hover:bg-white/70 hover:text-gray-900 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              aria-label={t('common.close', 'Close')}
            >
              <img src={CLOSE_ICON} alt="" className="h-8 w-8 object-contain" />
            </button>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <div
              key={`mobile-nav-${i18n.language}`}
              className="min-h-0 flex-1 overflow-y-auto px-3 py-4 space-y-1"
            >
              {user?.email && (
                <p className="px-3 pb-3 text-sm text-gray-600 break-all border-b border-primary-200/20 mb-3">
                  {localizeDigits(user.email, uiLang)}
                </p>
              )}
              <Link to="/dashboard" className={navLinkClass} onClick={() => setMobileMenuOpen(false)}>
                <img src="/assets/images/ui_elements/icon_home.png" alt="" className={navIconClass} />
                {t('common.home', 'Home')}
              </Link>
              <Link to="/draw" className={navLinkClass} onClick={() => setMobileMenuOpen(false)}>
                <img src="/assets/images/ui_elements/icon_draw.png" alt="" className={navIconClass} />
                {t('common.draw')}
              </Link>
              {user?.role === 'parent' && (
                <Link to="/children" className={navLinkClass} onClick={() => setMobileMenuOpen(false)}>
                  <img src="/assets/images/ui_elements/icon_profile.png" alt="" className={navIconClass} />
                  {t('common.navChildren', 'My children')}
                </Link>
              )}
              {(user?.role === 'school_admin' || user?.role === 'teacher') && (
                <Link to="/school/students" className={navLinkClass} onClick={() => setMobileMenuOpen(false)}>
                  <img src="/assets/images/ui_elements/icon_profile.png" alt="" className={navIconClass} />
                  {t('common.navSchoolStudents', 'School students')}
                </Link>
              )}
              <Link to="/album" className={navLinkClass} onClick={() => setMobileMenuOpen(false)}>
                <img src="/assets/images/ui_elements/icon_album.png" alt="" className={navIconClass} />
                {t('common.album')}
              </Link>
              <Link
                to="/dashboard/progress"
                className={navLinkClass}
                onClick={() => setMobileMenuOpen(false)}
              >
                <img src="/assets/images/ui_elements/icon_success.png" alt="" className={navIconClass} />
                {t('dashboard.navProgress')}
              </Link>
            </div>

            {/* Language + logout: outside scroll so the language dropdown is not clipped (overflow-y-auto) */}
            <div className="shrink-0 border-t border-primary-200/40 bg-emerald-50/40 px-4 py-3 backdrop-blur-sm">
              <p className="text-xs font-medium text-gray-500 mb-2">{t('common.language', 'Language')}</p>
              <LanguageSwitcher popoverPlacement="above" />
              <button
                type="button"
                className={`${navLinkClass} mt-3 w-full text-start border-t border-primary-200/25 pt-3`}
                onClick={handleLogout}
              >
                <img src="/assets/images/ui_elements/icon_settings.png" alt="" className={navIconClass} />
                {t('common.logout')}
              </button>
            </div>
          </div>
        </aside>
      </div>

      <main ref={mainScrollRef} className="flex flex-1 flex-col min-h-0 overflow-y-auto">
        <Outlet />
      </main>
    </div>
  )
}

export default Layout
